import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { UploadIcon } from "@icons";
import React, { useCallback, useRef } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { useEntities } from "@hooks/useEntities";
import { EntitySelect } from "@components/entity/EntitySelect";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { HStack } from "@design-system/HStack/HStack";
import { useAccountingTabContext } from "../../useAccountingTab";
import { CreateAccountSpreadsheet } from "./CreateAccountSpreadsheet";
import styles from "./CreateAccountSpreadsheet.module.scss";
import {
    AccountProxyObject,
    ValidationErrors,
} from "./CreateAccountSpreadsheetModal";
import { Footer } from "./Footer";

interface Props {
    loading: boolean;
    availableClassifications?: AccountClass[];
    onFileUpload?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    isUploadLoading?: boolean;
    accounts: AccountProxyObject[];
    onAccountsChange: (accounts: AccountProxyObject[]) => void;
    onSave: () => void;
    validationErrors: ValidationErrors;
    hasAttemptedSubmit: boolean;
}

export const CreateAccountSpreadsheetModalContent: React.FC<Props> = ({
    loading,
    availableClassifications,
    onFileUpload,
    isUploadLoading,
    accounts,
    onAccountsChange,
    onSave,
    validationErrors,
    hasAttemptedSubmit,
}) => {
    const scrollbarsRef = useRef<Scrollbars>(null);

    const linesRef = React.useRef(accounts);

    React.useEffect(() => {
        linesRef.current = accounts;
    }, [accounts]);

    const addLines = useCallback(() => {
        const currentLines = linesRef.current;
        const newLines = [
            ...currentLines,
            ...new Array(5).fill(null).map(() => ({
                id: Math.random().toString(),
                name: "",
                accountType: null,
                accountSubtype: null,
                mappedKickAccountId: null,
                parentGroupId: null,
            })),
        ];
        onAccountsChange(newLines);
    }, [linesRef, onAccountsChange]);

    const entities = useEntities();
    const { selectedEntity, setFilters } = useAccountingTabContext();

    return (
        <>
            <StandardModalHeader>Create Accounts</StandardModalHeader>
            <StandardModalBody className={styles.modalBody}>
                <HStack
                    gap={8}
                    justify="between"
                    align="center"
                    padding={24}
                    className={styles.modalBodyHeader}
                >
                    <EntitySelect
                        entities={entities}
                        selectedEntity={selectedEntity}
                        onChange={(entity) => {
                            setFilters((current) => ({
                                ...current,
                                entityId: entity?.id,
                            }));
                        }}
                    />
                    {onFileUpload && (
                        <>
                            <input
                                type="file"
                                id="coa-file-upload"
                                accept=".csv,.xlsx,.xls"
                                onChange={onFileUpload}
                                style={{ display: "none" }}
                            />
                            <ButtonWithLoader
                                size="md"
                                variant="primary"
                                onClick={() =>
                                    document
                                        .getElementById("coa-file-upload")
                                        ?.click()
                                }
                                loading={isUploadLoading ?? false}
                                disabled={isUploadLoading ?? false}
                                className={styles.uploadButton}
                                leftIcon={
                                    <UploadIcon
                                        className="icon-color-current"
                                        width="12"
                                        height="12"
                                    />
                                }
                            >
                                Upload File
                            </ButtonWithLoader>
                        </>
                    )}
                </HStack>
                {selectedEntity && (
                    <Scrollbars
                        className={styles.spreadsheetContainer}
                        style={{ height: "435px" }}
                        ref={scrollbarsRef}
                    >
                        <CreateAccountSpreadsheet
                            availableClassifications={availableClassifications}
                            accounts={accounts}
                            onAccountsChange={onAccountsChange}
                            validationErrors={validationErrors}
                            hasAttemptedSubmit={hasAttemptedSubmit}
                            entity={selectedEntity}
                        />
                    </Scrollbars>
                )}
            </StandardModalBody>

            <Footer
                loading={loading}
                addLines={addLines}
                onSave={onSave}
                accounts={accounts}
                validationErrors={validationErrors}
            />
        </>
    );
};
